import { useFormikContext } from 'formik'
import { keys, map, omit, prop } from 'ramda'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import sift from 'sift'
import styled from 'styled-components'
import { FieldInput, FieldDropdown, PhoneInput, Helper, FieldDatePicker } from '@/components/form'
import { StateDropdown } from '@/components/address-form'
import { breakpoints } from '@/styles'
import htmlToReact from '@/utils/html-to-react'
import {
  FeaturesSection,
  UtilitiesSection,
  PetPolicySection,
  ParkingSection,
  StorageSection,
  ExtraFeesList
} from './sections'
import {
  TextField,
  DateField,
  LeaseTypeField,
  StartDateField,
  EndDateField,
  LeaseEndActionField,
  PriceFrequencyField,
  PriceDateDescriptionField
} from './fields'
import { Wrapper, FormStack, FormGrid, SectionTitle, SectionSubtitle } from '../elements'

const FieldWrapper = styled.div`
  ${props =>
    props.fullWidth &&
    `
    grid-column: 1 / -1;
    max-width: calc(50% - 15px);

    @media screen and (max-width: ${breakpoints.phoneMax}) {
      max-width: unset;
    }
  `}
`

const TextWrapper = styled.div`
  p {
    font-size: 14px;
    letter-spacing: -0.2px;
    line-height: 19px;
    margin: -5px 0 0;
  }

  ${props =>
    props.fullWidth &&
    `
    grid-column: 1 / -1;
    p {
      margin: 0;
    }
  `}
`

const updateCustomFieldValue = (value, item, setFieldValue) => {
  setFieldValue(`custom_field_types.${item.name}`, item.field_type)
  setFieldValue(`${item.name}`, value.trimStart())
}

const removeCustomFieldValues = (fields, data, setValues) => {
  const currentFields = keys(data.custom_field_types)
  const fieldsToRemove = currentFields.filter(f => fields.includes(f))
  if (fieldsToRemove.length === 0) return

  setValues(() => {
    const customFieldTypes = omit(fieldsToRemove, data.custom_field_types)
    const formValues = omit([...fieldsToRemove, 'custom_field_types'], data)
    return { ...formValues, custom_field_types: customFieldTypes }
  })
}

const ContractTerms = ({ contractId, listingId, act, listing }) => {
  const { t, i18n } = useTranslation()
  const { setFieldValue, setValues, values: data } = useFormikContext()
  const isAlberta = act.state === 'AB' || listing?.building?.state === 'AB'

  const checkConditions = item => {
    if (!item.conditions) return item

    const cond = sift(item.conditions)
    const result = cond(data)

    // clear values of fields that are not conditionally displayed
    if (!result) {
      const itemFields = item.children ? map(prop('name'), item.children) : [item.name]
      removeCustomFieldValues(itemFields, data, setValues)
      return null
    }

    return item
  }

  const renderLayout = (items, level = 1) => {
    if (!items) return null
    return items.map(item => renderLayoutItem(item, level))
  }

  const renderSection = (item, level) => {
    switch (item.title) {
      case 'rent_info':
        return (
          <FormGrid key={item.title}>
            <SectionSubtitle>{t(`l.contract.edit.${item.title}.subtitle`)}</SectionSubtitle>
            {renderLayout(item.children, level + 1)}
          </FormGrid>
        )
      default:
        switch (level) {
          case 1:
            return (
              <FormStack key={item.title}>
                <SectionTitle>{t(`l.contract.edit.${item.title}.title`)}</SectionTitle>
                {renderLayout(item.children, level + 1)}
              </FormStack>
            )
          case 2:
            return (
              <FormGrid key={item.title}>
                <SectionSubtitle>{t(`l.contract.edit.${item.title}.subtitle`)}</SectionSubtitle>
                {renderLayout(item.children, level + 1)}
              </FormGrid>
            )
          case 3:
            return (
              <FormGrid key={item.title} span="true">
                <SectionSubtitle small>
                  {t(`l.contract.edit.${item.title}.subtitle`)}
                </SectionSubtitle>
                {renderLayout(item.children, level + 1)}
              </FormGrid>
            )
          case 4:
            return (
              <FormGrid key={item.title} span="true">
                <SectionSubtitle small>
                  {t(`l.contract.edit.${item.title}.subtitle`)}
                </SectionSubtitle>
                {renderLayout(item.children, level + 1)}
              </FormGrid>
            )
          default:
            return renderLayout(item.children, level + 1)
        }
    }
  }

  const renderStandardField = item => {
    const isPADAutomaticRequested = data['pad_requested']
    const standardFields = {
      lease_type: <LeaseTypeField isAlberta={isAlberta} />,
      start_date: <StartDateField data={data} />,
      end_date: <EndDateField data={data} />,
      lease_end_action: <LeaseEndActionField isAlberta={isAlberta} />,
      strata_lot_number: <TextField item={item} />,
      strata_plan_number: <TextField item={item} />,
      price: (
        <TextField
          item={item}
          prefix="$"
          required
          log
          transform={val => (val ?? '').replace(/[^0-9.]/g, '')}
        />
      ),
      price_frequency: (
        <PriceFrequencyField setFieldValue={setFieldValue} disabled={isPADAutomaticRequested} />
      ),
      price_date_description: <PriceDateDescriptionField data={data} />,
      security_deposit: (
        <TextField
          item={item}
          prefix="$"
          required
          transform={val => (val ?? '').replace(/[^0-9.]/g, '')}
        />
      ),
      security_deposit_due_date: <DateField item={item} />,
      contract_features: <FeaturesSection data={data} listingId={listingId} />,
      contract_utilities: <UtilitiesSection data={data} listingId={listingId} />,
      pet_policy: <PetPolicySection act={act} />,
      parking_info: <ParkingSection />,
      storage_info: <StorageSection />,
      extra_fees: <ExtraFeesList data={data.additional_fees} contractId={contractId} />
    }
    return (
      <FieldWrapper key={item.name} fullWidth={item.full_width}>
        {standardFields[item.name]}
        {item.helper && (
          <Helper
            text={item.helper.text}
            title={item.helper.title}
            description={item.helper.description}
          />
        )}
      </FieldWrapper>
    )
  }

  const getCustomInputType = item => {
    const customFieldName = `${item.name}`
    const placeholderKey = `l.contract.edit.${item.name}.placeholder`
    const placeholderText = i18n.exists(placeholderKey) ? t(placeholderKey) : item.placeholder

    const onChange = value => updateCustomFieldValue(value, item, setFieldValue)

    switch (item.input_type) {
      case 'phone_number':
        return (
          <PhoneInput
            name={customFieldName}
            placeholder={placeholderText}
            required={item.required}
            onChange={e => onChange(e.target.value)}
          />
        )
      case 'dropdown':
        return (
          <FieldDropdown
            name={customFieldName}
            placeholder={placeholderText}
            required={item.required}
            options={item.choices}
            onChange={e => onChange(e)}
          />
        )
      case 'dropdown_province':
        return (
          <StateDropdown
            name={customFieldName}
            country={act.country_code}
            required={item.required}
            onChange={e => onChange(e)}
          />
        )
      case 'date':
        return (
          <FieldDatePicker
            name={customFieldName}
            placeholder={placeholderText}
            required={item.required}
            onChange={value => onChange(value)}
          />
        )
      case 'text':
        return (
          <FieldInput
            name={customFieldName}
            placeholder={placeholderText}
            required={item.required}
            onChange={e => onChange(e.target.value)}
          />
        )
      default:
        return (
          <FieldInput
            name={customFieldName}
            placeholder={placeholderText}
            required={item.required}
            onChange={e => onChange(e.target.value)}
          />
        )
    }
  }

  const renderCustomField = item => (
    <FieldWrapper key={item.name} fullWidth={item.full_width}>
      {getCustomInputType(item)}
      {item.helper && (
        <Helper
          text={item.helper.text}
          title={item.helper.title}
          description={item.helper.description}
        />
      )}
    </FieldWrapper>
  )

  const renderText = item => {
    return (
      <TextWrapper key={item.name} fullWidth={item.full_width}>
        {htmlToReact(item.text)}
      </TextWrapper>
    )
  }

  const renderLayoutItem = (item, level) => {
    const layoutItem = checkConditions(item)
    if (!layoutItem) return null

    switch (item.type) {
      case 'SECTION':
        return renderSection(layoutItem, level)
      case 'FIELD':
        return renderStandardField(layoutItem)
      case 'CUSTOM_FIELD':
        return renderCustomField(layoutItem)
      case 'TEXT':
        return renderText(layoutItem)
      default:
        return null
    }
  }

  if (!data) return null

  return (
    <Wrapper sticky>
      <FormStack>{renderLayout(act.form_layout)}</FormStack>
    </Wrapper>
  )
}

export default memo(ContractTerms)
